"use client";

import { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { Star, Save, X } from "lucide-react";
import PropTypes from "prop-types";
import "./submission-editor.css";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Alert, Snackbar } from "@mui/material";

export function SubmissionEditor({ form, submission, onSave, onCancel }) {
  // console.log("Submission Form:", form);
  // console.log("Submissions Data:", submission);
  const [formData, setFormData] = useState({});
  const [state, setState] = useState({
    open: false,
    severity: "success",
    vertical: "top",
    horizontal: "right",
    message: "",
  });

  const handleClose = () => setState({ ...state, open: false });
  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    // console.log("🚀 ~ fetchUsers ~ response:", response);
    return response.data;
  };

  const {
    data: meData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["me"], // Unique key for the query
    queryFn: me, // The async function to run
    retry: false,
  });

  useEffect(() => {
    if (submission && form) {
      const initialData = {};
      form.fields.forEach((field) => {
        initialData[field.id] = submission[field.id] || "";
      });
      setFormData(initialData);
    }
  }, [submission, form]);

  const handleFieldChange = (fieldId, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const formatDateForMySQL = (isoDate) => {
    const d = new Date(isoDate);
    return d.toISOString().slice(0, 19).replace("T", " ");
  };

  const handleSave = async () => {
    try {
      // Prepare payload
      const payload = {
        form: {
          id: form.id,
          title: form.title,
          description: form.description,
          createdAt: formatDateForMySQL(form.createdAt),
          created_by: form.created_by,
          fields: form.fields, // only fields, no submissions
        },
        submission: {
          ...submission,
          ...formData, // updated field values
          updatedAt: new Date().toISOString(),
          updated_by: meData?.user_id,
          district: meData?.district,
        },
      };


      // Send to backend
      const res = await axios.put(`${process.env.REACT_APP_API_URL}/submissions/update`, payload, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });

      if (res.data.status === 200) {
        setState({
          ...state,
          open: true,
          severity: "success",
          message: res.data.message || "Submission updated successfully",
        });
        // Wait for snackbar to show, then call onSave
        setTimeout(() => {
          onSave(payload.submission);
        }, 1500); 

      } else {
        console.error("Failed to update submission", res.data);

        // Show error snackbar
        setState({
          ...state,
          open: true,
          severity: "error",
          message: res.data.message || "Failed to update submission",
        });
      }
    } catch (err) {
      console.error("Error updating submission:", err);
      if (err.response) console.error("Response:", err.response.data);

      setState({
        ...state,
        open: true,
        severity: "error",
        message: "Failed to update submission",
      });
    }
  };

  const renderField = (field) => {
    const value = formData[field.id] || "";

    switch (field.type) {
      case "textarea":
        return (
          <Textarea
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            className="submission-input"
          />
        );

      case "select":
        return (
          <Select value={value} onValueChange={(val) => handleFieldChange(field.id, val)}>
            <SelectTrigger className="submission-select">
              <SelectValue placeholder={field.placeholder || "Select an option"} />
            </SelectTrigger>
            <SelectContent className="submission-select-dropdown">
              {field.options?.map((option, index) => (
                <SelectItem key={index} value={option} className="submission-select-item">
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case "radio":
        return (
          <RadioGroup value={value} onValueChange={(val) => handleFieldChange(field.id, val)}>
            {field.options?.map((option, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "6px", // spacing between each option
                  minHeight: "28px", // makes row slightly taller
                }}
              >
                <RadioGroupItem value={option} id={`${field.id}-${index}`} />
                <Label htmlFor={`${field.id}-${index}`} className="submission-radio-item">
                  {option}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );

      case "checkbox":
        const checkboxValues = Array.isArray(value) ? value : [];
        return (
          <div className="submission-radio-group">
            {field.options?.map((option, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "6px", // spacing between each option
                  minHeight: "28px", // makes row slightly taller
                }}
              >
                <Checkbox
                  id={`${field.id}-${index}`}
                  checked={checkboxValues.includes(option)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      handleFieldChange(field.id, [...checkboxValues, option]);
                    } else {
                      handleFieldChange(
                        field.id,
                        checkboxValues.filter((v) => v !== option)
                      );
                    }
                  }}
                />
                <Label htmlFor={`${field.id}-${index}`} className="submission-radio-item">
                  {option}
                </Label>
              </div>
            ))}
          </div>
        );

      case "rating":
        return (
          <div className="submission-rating">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`submission-rating-star ${star <= (value || 0) ? "active" : ""}`}
                onClick={() => handleFieldChange(field.id, star)}
              />
            ))}
          </div>
        );

      default:
        return (
          <Input
            type={field.type}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            className="submission-input"
          />
        );
    }
  };

  return (
    <Card className="submission-renderer-card">
      <Snackbar
        open={state.open}
        autoHideDuration={1500}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleClose} severity={state.severity} variant="filled">
          {state.message}
        </Alert>
      </Snackbar>
      <CardHeader className="submission-renderer-header">
        <CardTitle className="submission-renderer-title">
          <Save className="icon" />
          Edit Submission
        </CardTitle>
        <CardDescription className="submission-renderer-desc">
          Modify the submitted data for &quot;{form.title}&quot;
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {form.fields?.map((field) => (
            <div key={field.id} className="submission-field">
              <Label htmlFor={field.id} className="submission-label">
                {field.label}
                {field.required && <span className="submission-required">*</span>}
              </Label>
              {renderField(field)}
            </div>
          ))}
        </div>

        <div className="flex gap-2 pt-4">
          <Button onClick={handleSave} className="btn btn-primary submission-submit">
            <Save className="icon" />
            Save Changes
          </Button>
          <Button variant="outline" onClick={onCancel} className="btn btn-danger submission-submit">
            <X className="icon" />
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

SubmissionEditor.propTypes = {
  form: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    created_by: PropTypes.string,
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        required: PropTypes.bool,
        placeholder: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.string),
      })
    ).isRequired,
  }).isRequired,
  submission: PropTypes.shape({
    id: PropTypes.string,
    data: PropTypes.object,
    submittedAt: PropTypes.string,
    updatedAt: PropTypes.string,
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
